import React from 'react';
import { Box, Chip, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

const AVAILABLE_VARIABLES = [
  '{{company_name}}',
  '{{total_past_due}}',
  '{{worst_aging_days}}',
  '{{invoice_count}}',
  '{{account_no}}',
  '{{friendly_threshold_days}}',
  '{{second_notice_threshold_days}}',
  '{{invoice_table}}',
];

interface Props {
  initialContent: string;
  onChange: (html: string) => void;
  showVariables?: boolean;
}

// Reject javascript:/data:/vbscript: URIs at the editor layer — the backend sanitizer also
// strips them, but a malicious href would otherwise render as a live clickable link inside
// the editor DOM before submission.
const isSafeLinkHref = (href: string): boolean => /^(https?:|mailto:|tel:)/i.test(href);

const EmailBodyEditor: React.FC<Props> = ({ initialContent, onChange, showVariables = true }) => {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false, validate: isSafeLinkHref })],
    content: initialContent,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  if (!editor) return null;

  const insertVariable = (variable: string) => {
    editor.chain().focus().insertContent(variable).run();
  };

  const promptForLink = () => {
    const url = window.prompt('Link URL', editor.getAttributes('link').href || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    if (!isSafeLinkHref(url)) {
      window.alert('Only http(s), mailto:, and tel: URLs are allowed.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small">
          <Tooltip title="Bold">
            <ToggleButton
              value="bold"
              selected={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Italic">
            <ToggleButton
              value="italic"
              selected={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Bulleted list">
            <ToggleButton
              value="ul"
              selected={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulletedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Numbered list">
            <ToggleButton
              value="ol"
              selected={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumberedIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
        <Tooltip title="Insert / edit link">
          <IconButton size="small" onClick={promptForLink}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {showVariables && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>Available variables (click to insert):</Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {AVAILABLE_VARIABLES.map((v) => (
              <Chip key={v} label={v} size="small" onClick={() => insertVariable(v)} clickable />
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 1.5,
          minHeight: 300,
          '& .ProseMirror': { minHeight: 280, outline: 'none' },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default EmailBodyEditor;
